// --- PlayerPicker.jsx ---
import { motion } from "framer-motion";
import { Search, X, Eye, EyeOff, Shuffle, User, Tag, AlertTriangle } from "lucide-react";
import React from "react";
import { useTranslation } from 'react-i18next'

export default function PlayerPicker({
  players,            // array de noms OU [{ name, position }]
  statusByName = {},  // { [name]: { kind: "at-risk" | "monitored", sinceShort?: string, sinceFull?: string } }
  activeMap,          // { [name]: bool }
  onToggle,           // (name) => void
  onAll, onNone, onInvert,
  getStyleForPlayer,  // (name) => { color, dash }
  isDark,
  compact = false,
  defaultPosition = "all",
}) {
  const { t } = useTranslation();
  const translatePosition = (pos) => {
    const map = {
      'Defense':  t('positions.defense'),
      'Midfield': t('positions.midfield'),
      'Forward':  t('positions.forward'),
      'Lateral':  t('positions.lateral'),
    };
    return map[pos] || pos;
  };
  const shortPosition = (pos) => translatePosition(pos).slice(0, 3).toUpperCase();

  const [q, setQ] = React.useState("");
  const [positionFilter, setPositionFilter] = React.useState("all");
  const [activeTag, setActiveTag] = React.useState("all");

  const normalizedPlayers = React.useMemo(
    () => (players || []).map((p) => typeof p === "string" ? { name: p, position: undefined } : p),
    [players]
  );

  const positions = React.useMemo(
    () => Array.from(
      new Set(normalizedPlayers.map((p) => p.position).filter((pos) => !!pos && pos !== "N/A"))
    ).sort((a, b) => a.localeCompare(b)),
    [normalizedPlayers]
  );

  React.useEffect(() => {
    if (!positions.length) return;
    const targetPosition = positions.includes(defaultPosition) ? defaultPosition : "all";
    if (targetPosition !== "all") {
      setActiveTag((prev) => (prev === "all" ? targetPosition : prev));
      setPositionFilter((prev) => (prev === "all" ? targetPosition : prev));
    }
  }, [positions, defaultPosition]);

  const list = React.useMemo(() => {
    let l = normalizedPlayers.slice();
    if (positionFilter !== "all") l = l.filter((p) => p.position === positionFilter);
    if (q) {
      const qLower = q.toLowerCase();
      l = l.filter((p) => p.name.toLowerCase().includes(qLower));
    }
    return l.sort((a, b) => a.name.localeCompare(b.name));
  }, [normalizedPlayers, q, positionFilter]);

  const activeCount = React.useMemo(
    () => Object.values(activeMap || {}).filter(Boolean).length,
    [activeMap]
  );
  const totalCount = normalizedPlayers.length;

  const soloPlayer = React.useMemo(() => {
    const actives = Object.entries(activeMap || {}).filter(([, v]) => v);
    return actives.length === 1 ? actives[0][0] : null;
  }, [activeMap]);

  const handleSolo = (name) => {
    onNone && onNone();
    onToggle && onToggle(name);
  };

  const applyTag = (tag) => {
    setActiveTag(tag);
    if (tag === "all") {
      setPositionFilter("all");
      onAll && onAll();
      return;
    }
    setPositionFilter(tag);
    const namesForTag = normalizedPlayers.filter((p) => p.position === tag).map((p) => p.name);
    if (!namesForTag.length) return;
    onNone && onNone();
    namesForTag.forEach((name) => { onToggle && onToggle(name); });
  };

  return (
    <div className="w-full h-full flex flex-col gap-1.5">
      {/* Header: count + bulk action icons */}
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          <span className="text-indigo-500 font-semibold">{activeCount}</span>
          <span className="opacity-70">/{totalCount}</span>
          {" "}{t('player_picker.selected')}
        </span>
        <div className="flex items-center gap-0.5">
          <button
            type="button" onClick={onAll} title={t('player_picker.all')}
            className={`p-1 rounded-md transition ${isDark ? "hover:bg-white/10 text-gray-500 hover:text-emerald-400" : "hover:bg-black/5 text-gray-400 hover:text-emerald-600"}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            type="button" onClick={onNone} title={t('player_picker.none')}
            className={`p-1 rounded-md transition ${isDark ? "hover:bg-white/10 text-gray-500 hover:text-red-400" : "hover:bg-black/5 text-gray-400 hover:text-red-500"}`}
          >
            <EyeOff className="h-3.5 w-3.5" />
          </button>
          <button
            type="button" onClick={onInvert} title={t('player_picker.invert')}
            className={`p-1 rounded-md transition ${isDark ? "hover:bg-white/10 text-gray-500 hover:text-indigo-400" : "hover:bg-black/5 text-gray-400 hover:text-indigo-600"}`}
          >
            <Shuffle className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition-colors ${
        isDark
          ? "bg-gray-800/60 border-gray-700/80 focus-within:border-indigo-500/50"
          : "bg-gray-50 border-gray-200 focus-within:border-indigo-400/50"
      }`}>
        <Search className={`h-3.5 w-3.5 shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('player_picker.search')}
          className={`flex-1 text-[11px] bg-transparent outline-none ${
            isDark ? "placeholder-gray-500 text-white" : "placeholder-gray-400 text-gray-900"
          }`}
        />
        {q && (
          <button type="button" onClick={() => setQ("")} className={`${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}>
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Position filter chips */}
      {positions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => applyTag("all")}
            className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${
              activeTag === "all"
                ? isDark ? "bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/30" : "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-300/50"
                : isDark ? "text-gray-500 hover:bg-white/5 hover:text-gray-300" : "text-gray-400 hover:bg-black/5 hover:text-gray-700"
            }`}
          >
            {t('positions.all')}
          </button>
          {positions.map((pos) => (
            <button
              key={pos}
              type="button"
              onClick={() => applyTag(pos)}
              className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${
                activeTag === pos
                  ? isDark ? "bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/30" : "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-300/50"
                  : isDark ? "text-gray-500 hover:bg-white/5 hover:text-gray-300" : "text-gray-400 hover:bg-black/5 hover:text-gray-700"
              }`}
            >
              {translatePosition(pos)}
            </button>
          ))}
        </div>
      )}

      {/* Player list */}
      <div className="flex-1 overflow-y-auto min-h-0 -mx-0.5 px-0.5" style={{ maxHeight: "320px" }}>
        {list.map((player) => {
          const name = player.name;
          const pos = player.position;
          const status = statusByName?.[name] || null;
          const isAtRisk = status?.kind === "at-risk";
          const isMonitored = status?.kind === "monitored";
          const { color } = getStyleForPlayer(name);
          const on = !!activeMap[name];
          const isSolo = soloPlayer === name;
          const statusDotRing = isAtRisk
            ? (isDark ? "0 0 0 2px rgba(251,113,133,0.55)" : "0 0 0 2px rgba(225,29,72,0.35)")
            : isMonitored
              ? (isDark ? "0 0 0 2px rgba(251,191,36,0.55)" : "0 0 0 2px rgba(217,119,6,0.35)")
              : "none";
          const monitoredTooltip = t('charts.monitored_since_full', {
            date: status?.sinceFull || t('common.na', { defaultValue: 'N/A' }),
            defaultValue: `Monitored since ${status?.sinceFull || 'N/A'}`,
          });
          const atRiskTooltip = t('charts.risk_badge_at_risk', { defaultValue: 'At-risk' });
          const dotTitle = [
            on ? t('player_picker.hide') : t('player_picker.show'),
            isMonitored ? monitoredTooltip : isAtRisk ? atRiskTooltip : null,
          ].filter(Boolean).join(' • ');

          return (
            <motion.div
              key={name}
              initial={false}
              animate={{ opacity: on ? 1 : 0.4 }}
              transition={{ duration: 0.15 }}
              className={`group flex items-center gap-1.5 px-1.5 py-[5px] rounded-md cursor-default transition-colors ${
                isDark ? "hover:bg-white/5" : "hover:bg-black/[0.03]"
              }`}
            >
              {/* Color swatch — click to toggle visibility */}
              <button
                type="button"
                onClick={() => onToggle(name)}
                className="relative shrink-0 flex items-center justify-center w-4 h-4 rounded transition-transform hover:scale-110"
                title={dotTitle}
              >
                <span
                  className={`block w-2.5 h-2.5 rounded-[3px] transition-all ${on ? "" : "ring-1 ring-inset"}`}
                  style={{
                    backgroundColor: on ? color : "transparent",
                    ringColor: color,
                    boxShadow: statusDotRing,
                    ["--tw-ring-color"]: color,
                  }}
                />

                {isMonitored && (
                  <span
                    className={`pointer-events-none absolute left-full top-1/2 ml-1 -translate-y-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[9px] font-semibold opacity-0 transition-opacity group-hover:opacity-100 ${
                      isDark
                        ? "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40"
                        : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                    }`}
                  >
                    {t('charts.risk_badge_monitored', { defaultValue: 'Monitored' })}
                    {status?.sinceShort ? ` ${status.sinceShort}` : ''}
                  </span>
                )}
              </button>

              {/* Player name — click to solo select */}
              <button
                type="button"
                onClick={() => handleSolo(name)}
                className={`flex-1 text-left text-[11px] leading-tight truncate transition-colors ${
                  isSolo
                    ? "font-semibold text-indigo-400"
                    : on
                      ? isDark ? "text-gray-200" : "text-gray-700"
                      : isDark ? "text-gray-500" : "text-gray-400"
                }`}
                title={t('player_picker.solo_tooltip', { name })}
              >
                {name}
              </button>

              {isAtRisk && (
                <span
                  className={`shrink-0 rounded p-0.5 ${
                    isDark
                      ? "text-rose-300"
                      : "text-rose-600"
                  }`}
                  title={atRiskTooltip}
                >
                  <AlertTriangle className="h-3 w-3" />
                </span>
              )}

              {isMonitored && (
                <span
                  className={`shrink-0 rounded p-0.5 ${
                    isDark
                      ? "text-amber-300"
                      : "text-amber-600"
                  }`}
                  title={monitoredTooltip}
                >
                  <Tag className="h-3 w-3" />
                </span>
              )}

              {/* Abbreviated position */}
              {pos && (
                <span className={`text-[9px] font-medium uppercase tracking-wider shrink-0 ${
                  isDark ? "text-gray-600" : "text-gray-300"
                }`}>
                  {shortPosition(pos)}
                </span>
              )}

              {/* Solo icon on hover */}
              <User className={`h-3 w-3 shrink-0 transition-opacity ${
                isSolo
                  ? "opacity-60 text-indigo-400"
                  : "opacity-0 group-hover:opacity-40"
              } ${isDark ? "text-gray-400" : "text-gray-500"}`} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
